import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Plus } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { cn } from '@/lib/utils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { getOrganizationMembers } from '@/lib/firebase/queries/organizations/getOrganizationMembers';
import { getOrganizationManagers } from '@/lib/firebase/queries/organizations/managers';
import { orgCandidates } from '@/lib/groups/groupCandidates';
import {
  getGroupMembers,
  addGroupMember,
  removeGroupMember,
  SectionNotJoinableError,
  SubgroupConflictError,
} from '@/lib/firebase/queries/group-members';
import { getGroupMembersVirtual } from '@/lib/firebase/queries/virtual-membership';
import { syncOrganizationManagersToGroups } from '@/lib/firebase/queries/groups';
import { toast } from 'sonner';
import type { User } from '@/types';
import type { Group } from '@/types/support';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface OrganizationGroupingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  groups: Group[];
}

export function OrganizationGroupingModal({ open, onOpenChange, organizationId, groups }: OrganizationGroupingModalProps) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  // Org managers: listed among a group's members (owner decision #2, mobile
  // parity) but never offered as candidates — they join every group
  // automatically (syncOrganizationManagersToGroups).
  const [managerIds, setManagerIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [groupMembers, setGroupMembers] = useState<Record<string, string[]>>({});
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('members');

  useEffect(() => {
    if (open) {
      // Auto-sync managers when modal opens
      syncOrganizationManagersToGroups(organizationId).catch(error => 
        console.warn('⚠️ Auto-sync in modal failed:', error)
      );
      
      loadUsers();
      loadGroupMembers();
    }
  }, [open, organizationId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Owner decision #3: candidates are the organization's beneficiaries
      // only — the manager screens' query — never the whole `users` collection.
      const [beneficiaries, managers] = await Promise.all([
        getOrganizationMembers(organizationId),
        getOrganizationManagers(organizationId),
      ]);
      const ids = new Set(managers.map(m => m.id));
      const people = orgCandidates(beneficiaries || [], ids) as unknown as User[];
      setManagerIds(ids);
      setUsers([...people, ...managers]);
    } catch (error) {
      console.error('❌ Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

    const loadGroupMembers = async () => {
    try {
      const members: Record<string, string[]> = {};
      await Promise.all(
        groups.map(async (group) => {
          // 🎯 Use virtual membership system for consistency with hierarchy
          const groupUsers = await getGroupMembersVirtual(group.id);
          members[group.id] = groupUsers.map(user => user.id);
          console.log(`🔄 Loaded ${groupUsers.length} virtual members for group "${group.name}" (${group.id})`);
        })
      );
      setGroupMembers(members);
    } catch (error) {
      console.error('Error loading group members:', error);
    }
  };

  // Best-effort display name for a group that may not be in the `groups`
  // prop (e.g. the conflicting subgroup a user already belongs to, when this
  // modal is only showing one other group's column).
  const getGroupDisplayName = async (groupId: string): Promise<string> => {
    const known = groups.find(g => g.id === groupId);
    if (known) return known.name;
    try {
      const snap = await getDoc(doc(db, 'supportGroups', groupId));
      return snap.exists() ? ((snap.data() as any).name || groupId) : groupId;
    } catch {
      return groupId;
    }
  };

  const handleToggleMember = async (groupId: string, userId: string) => {
    if (!user) return;

    const targetGroup = groups.find(g => g.id === groupId);
    if (targetGroup?.groupType === 'parent') {
      // Defense in depth: the UI already hides this action for sections.
      toast.error(language === 'ar' ? 'لا يمكن الانضمام إلى الأقسام مباشرة' : 'Sections cannot be joined directly');
      return;
    }

    try {
      setUpdating(true);

      // 🛡️ Check if the user is an organization manager (they should not appear in this list anyway)
      const { isOrganizationManager } = await import('@/lib/firebase/queries/organizations/isOrganizationMember');
      const isTargetOrgManager = await isOrganizationManager(organizationId, userId);
      if (isTargetOrgManager) {
        console.log('🛡️ Cannot manually manage organization manager membership');
        toast.info('Organization managers are automatically members of all groups');
        return;
      }

      const isMember = groupMembers[groupId]?.includes(userId);

      if (isMember) {
        const success = await removeGroupMember(groupId, userId, user.uid);
        if (success) {
          setGroupMembers(prev => ({
            ...prev,
            [groupId]: prev[groupId].filter(id => id !== userId)
          }));
          toast.success('Member removed successfully! 💪');
          // ✅ No manual sync needed - member counts are calculated dynamically
        } else {
          toast.error('Failed to remove member');
        }
        return;
      }

      // Adding: a user may belong to at most one subgroup per organization.
      // addGroupMember refuses with SubgroupConflictError, naming the
      // existing group, if this would create a second one — confirm the
      // move (destructive: removes the old membership) then retry.
      try {
        const success = await addGroupMember(groupId, userId, user.uid);
        if (success) {
          setGroupMembers(prev => ({
            ...prev,
            [groupId]: [...(prev[groupId] || []), userId]
          }));
          toast.success('Member added successfully! 💪');
          // ✅ No manual sync needed - member counts are calculated dynamically
        } else {
          toast.error('Failed to add member');
        }
      } catch (innerError) {
        if (innerError instanceof SubgroupConflictError) {
          const existingName = await getGroupDisplayName(innerError.existingGroupId);
          const newName = targetGroup?.name || '';
          const confirmMove = window.confirm(
            language === 'ar'
              ? `سيتم نقل هذا العضو من "${existingName}" إلى "${newName}". هل تريد المتابعة؟`
              : `This will move this member from "${existingName}" to "${newName}". Continue?`
          );
          if (!confirmMove) return;

          const removed = await removeGroupMember(innerError.existingGroupId, userId, user.uid);
          if (removed) {
            setGroupMembers(prev => ({
              ...prev,
              [innerError.existingGroupId]: (prev[innerError.existingGroupId] || []).filter(id => id !== userId)
            }));
          }

          const retrySuccess = await addGroupMember(groupId, userId, user.uid);
          if (retrySuccess) {
            setGroupMembers(prev => ({
              ...prev,
              [groupId]: [...(prev[groupId] || []), userId]
            }));
            toast.success('Member added successfully! 💪');
          } else {
            toast.error('Failed to add member');
          }
        } else if (innerError instanceof SectionNotJoinableError) {
          toast.error(language === 'ar' ? 'لا يمكن الانضمام إلى الأقسام مباشرة' : 'Sections cannot be joined directly');
        } else {
          throw innerError;
        }
      }
    } catch (error) {
      console.error('Error updating group member:', error);
      // Show specific message for organization manager protection
      if (error instanceof Error && error.message.includes('Organization managers cannot be removed')) {
        toast.error('Organization managers cannot be removed from groups');
      } else {
        toast.error('Failed to update group membership');
      }
    } finally {
      setUpdating(false);
    }
  };

  // Get all assigned users across all groups
  const getAllAssignedUsers = () => {
    const assignedUsers = new Set<string>();
    Object.values(groupMembers).forEach(memberIds => {
      memberIds.forEach(id => assignedUsers.add(id));
    });
    return assignedUsers;
  };

  // Filter users based on assignment status
  const getFilteredUsers = (assignedOnly: boolean = false) => {
    const assignedUsers = getAllAssignedUsers();
    const filtered = users.filter(user => {
      if (!user) return false;
      const isAssigned = assignedUsers.has(user.id);
      return assignedOnly ? isAssigned : !isAssigned && !managerIds.has(user.id);
    });
    
    // Debug logging
    if (!assignedOnly) {
      console.log('🔍 Unassigned users filtering:', {
        totalUsers: users.length,
        assignedUsers: assignedUsers.size,
        unassignedUsers: filtered.length,
        assignedUserIds: Array.from(assignedUsers)
      });
    }
    
    return filtered;
  };

  const currentMembers = getFilteredUsers(true);
  const unassignedUsers = getFilteredUsers(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "sm:max-w-[800px] h-[80vh] flex flex-col",
        "[&>button.absolute.right-4.top-4]:hidden",
        language === 'ar' ? 'rtl' : 'ltr'
      )} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <DialogHeader className={language === 'ar' ? 'text-right' : 'text-left'}>
          <DialogTitle className={cn(
            "text-xl",
            language === 'ar' ? 'text-right' : 'text-left'
          )}>
            {groups.length === 1 
                      ? `${t('dashboard.manager.groups.actions.manageMembers')}: ${groups[0].name}`
        : t('dashboard.manager.groups.actions.manage')
            }
          </DialogTitle>
        </DialogHeader>

        <div className={cn(
          "flex-1 overflow-y-auto space-y-4",
          language === 'ar' ? 'rtl' : 'ltr'
        )}>
          <Tabs value={activeTab} onValueChange={setActiveTab} dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <TabsList className={cn(
              "grid w-full grid-cols-2",
              language === 'ar' ? 'flex-row-reverse' : ''
            )}>
              <TabsTrigger value="members">
                {language === 'ar' ? `أعضاء المجموعة (${currentMembers.length})` : `Group Members (${currentMembers.length})`}
              </TabsTrigger>
              <TabsTrigger value="unassigned">
                {language === 'ar' ? `مستفيدين المنظمة (${unassignedUsers.length})` : `Organization Customers (${unassignedUsers.length})`}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="members" className="mt-4">
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={language === 'ar' ? 'text-right' : 'text-left'}>
                        {language === 'ar' ? 'اسم العضو' : 'Member Name'}
                      </TableHead>
                      {groups.length === 1 ? (
                        <TableHead className={language === 'ar' ? 'text-right' : 'text-left'}>
                          {language === 'ar' ? 'الإجراءات' : 'Action'}
                        </TableHead>
                      ) : (
                        groups.map(group => (
                          <TableHead key={group.id} className={language === 'ar' ? 'text-right' : 'text-left'}>
                            {group.name}
                          </TableHead>
                        ))
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={groups.length + 1} className={cn(
                          "py-8",
                          language === 'ar' ? 'text-right' : 'text-center'
                        )}>
                          <div className={cn(
                            "flex items-center",
                            language === 'ar' ? 'justify-start' : 'justify-center'
                          )}>
                            <Loader2 className="h-8 w-8 animate-spin" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : currentMembers.length === 0 && unassignedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={groups.length + 1} className={cn(
                          "py-8 text-muted-foreground",
                          language === 'ar' ? 'text-right' : 'text-center'
                        )}>
                          <div className="space-y-2">
                            <p>No organization members found</p>
                            <p className="text-xs">Organization ID: {organizationId}</p>
                            <p className="text-xs">Groups: {groups.length}</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentMembers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className={cn(
                            language === 'ar' ? 'text-right' : 'text-start',
                            language === 'ar' ? 'pr-4' : 'pl-4'
                          )}>
                            <div className={cn(
                              "flex items-center gap-2",
                              language === 'ar' ? 'flex-row-reverse justify-end' : 'justify-start'
                            )}>
                              <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                                <div className={cn(
                                  "font-medium",
                                  language === 'ar' ? 'text-right' : 'text-left'
                                )}>
                                  {language === 'en' 
                                    ? `${user.firstNameEn} ${user.lastNameEn}`
                                    : `${user.firstNameAr} ${user.lastNameAr}`
                                  }
                                </div>

                              </div>
                            </div>
                          </TableCell>
                          {groups.length === 1 ? (
                            <TableCell className={cn(
                            "text-center",
                            language === 'ar' ? 'text-right' : ''
                          )}>
                              {groups[0].groupType === 'parent' ? (
                                <span className="text-xs text-muted-foreground">
                                  {language === 'ar' ? 'الأقسام مفاهيمية — لا انضمام مباشر' : 'Sections are conceptual — not joinable'}
                                </span>
                              ) : (
                                <Button
                                  variant={groupMembers[groups[0].id]?.includes(user.id) ? "destructive" : "outline"}
                                  size="sm"
                                  onClick={() => handleToggleMember(groups[0].id, user.id)}
                                  disabled={updating}
                                >
                                  {groupMembers[groups[0].id]?.includes(user.id) ? (
                                    t('dashboard.admin.groups.removeMember')
                                  ) : (
                                    t('dashboard.admin.groups.addMember')
                                  )}
                                </Button>
                              )}
                            </TableCell>
                          ) : (
                            groups.map(group => (
                              <TableCell key={group.id} className={language === 'ar' ? 'text-right' : 'text-left'}>
                                {group.groupType === 'parent' ? (
                                  <span className="text-xs text-muted-foreground">
                                    {language === 'ar' ? 'قسم — لا انضمام مباشر' : 'Section — not joinable'}
                                  </span>
                                ) : (
                                  <Button
                                    variant={groupMembers[group.id]?.includes(user.id) ? "destructive" : "outline"}
                                    size="sm"
                                    onClick={() => handleToggleMember(group.id, user.id)}
                                    disabled={updating}
                                  >
                                    {groupMembers[group.id]?.includes(user.id) ? (
                                      t('dashboard.admin.groups.removeMember')
                                    ) : (
                                      t('dashboard.admin.groups.addMember')
                                    )}
                                  </Button>
                                )}
                              </TableCell>
                            ))
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="unassigned" className="mt-4">
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={language === 'ar' ? 'text-right' : 'text-left'}>
                        {language === 'ar' ? 'اسم العضو' : 'Member Name'}
                      </TableHead>
                      <TableHead className={language === 'ar' ? 'text-right' : 'text-left'}>{t('dashboard.manager.groups.table.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={2} className={cn(
                          "py-8",
                          language === 'ar' ? 'text-right' : 'text-center'
                        )}>
                          <div className={cn(
                            "flex items-center",
                            language === 'ar' ? 'justify-start' : 'justify-center'
                          )}>
                            <Loader2 className="h-8 w-8 animate-spin" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : unassignedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className={cn(
                          "py-8 text-muted-foreground",
                          language === 'ar' ? 'text-right' : 'text-center'
                        )}>
                          {t('dashboard.manager.groups.table.noGroups')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      unassignedUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className={cn(
                            language === 'ar' ? 'text-right' : 'text-start',
                            language === 'ar' ? 'pr-4' : 'pl-4'
                          )}>
                            <div className={cn(
                              "flex items-center gap-2",
                              language === 'ar' ? 'flex-row-reverse justify-end' : 'justify-start'
                            )}>
                              <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                                <div className={cn(
                                  "font-medium",
                                  language === 'ar' ? 'text-right' : 'text-left'
                                )}>
                                  {language === 'en' 
                                    ? `${user.firstNameEn} ${user.lastNameEn}`
                                    : `${user.firstNameAr} ${user.lastNameAr}`
                                  }
                                </div>

                              </div>
                            </div>
                          </TableCell>
                          <TableCell className={cn(
                            "text-center",
                            language === 'ar' ? 'text-right' : ''
                          )}>
                            {groups[0]?.groupType === 'parent' ? (
                              <span className="text-xs text-muted-foreground">
                                {language === 'ar' ? 'الأقسام مفاهيمية — لا انضمام مباشر' : 'Sections are conceptual — not joinable'}
                              </span>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (groups.length > 0) {
                                    handleToggleMember(groups[0].id, user.id);
                                  }
                                }}
                                disabled={updating || groups.length === 0}
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                {t('dashboard.admin.groups.addMember')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
                            {t('dashboard.manager.groups.delete.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 